import { defineStore } from 'pinia'

// import { message } from 'ant-design-vue'
import type { MenuData } from '../../types/app'

import { rootRoute } from '@/router/constant'
import type { Authorization, Role } from '@/types/models'
import type { AuthUser } from '@/types/auth.ts'
import { generateFlatRoutes, generateRoutes } from '@/router/generate-route.ts'

export const useAuth = defineStore('auth', () => {
  // State
  const user = ref<AuthUser | null>(null)
  const isAuthenticated = ref(false)
  const isRefreshingToken = ref(false)
  const showModalChangePassword = ref(false)
  const mqttToken = ref<string | null>(null)
  const memberCompanyId = ref<string | null>(null)
  const memberCompanyCode = ref<string | null>(null)
  const menuData = shallowRef<MenuData>([])
  const routerData = shallowRef<any>()

  // Getters
  const isSignedIn = computed(() => isAuthenticated.value)

  // Actions
  function setUser(payload: AuthUser | null) {
    user.value = payload
  }
  function setShowModalChangePassword(payload: boolean) {
    showModalChangePassword.value = payload
  }
  function setMqttToken(val: string | null) {
    mqttToken.value = val
  }
  function setMemberCompanyId(val: string | null) {
    memberCompanyId.value = val
  }
  function setMemberCompanyCode(val: string | null) {
    memberCompanyCode.value = val
  }

  async function getAuthInfo() {
    const res = await usePost(`${import.meta.env.VITE_APP_URL}/api/auth/info`)
    console.log('Auth info response:', res)
    if (res.body.accessToken && res.body.username && res.body.mqttToken) {
      setMqttToken(res.body.mqttToken)
      setMemberCompanyId(res.body.memberCompanyId)
      setMemberCompanyCode(res.body.memberCompanyCode)
      isAuthenticated.value = true
      return res.body
    } else {
      isAuthenticated.value = false
      setUser(null)
      return null
    }
  }

  async function getUser() {
    // const tk = access_token || token.value
    try {
      const res = await usePost(
        `/iam/user/userInfo`
        /*{ access_token: tk }
        {
          headers: {
            Authorization: `Bearer ${tk}`,
          },
        }*/
      )
      const userInfo = {
        id: res.body.userId,
        name: res.body.fullName,
        username: res.body.userName,
        email: res.body.email,
        staffId: res.body.staffId,
        telegramId: res.body.telegramId,
        phone: res.body.mobile,
        roles: res.body.listRole?.map((item: Role) => item.code) || [],
        uris:
          res.body.authorization
            ?.filter((item: Authorization) => item.type === 'web')
            .map((item: Authorization) => item.uri.replace(/\u200B/g, '')) || [],
      }
      setUser(userInfo)
      isAuthenticated.value = true
      showModalChangePassword.value = res.body.rqrChangePwd || false
      return userInfo
    } catch (error) {
      console.error('Error fetching user info:', error)
      throw error
    }
  }

  function hasUri(uri: string): boolean {
    if (!user.value || !user.value.uris) {
      return false
    }
    return user.value.uris.includes(uri)
  }

  async function changePassword() {
    try {
      const res = await usePost('/iam/user/request-change-pwd')
      showModalChangePassword.value = false
      return res.body
    } catch (error) {
      console.error('Error changing password:', error)
      throw error
    }
  }

  async function logout() {
    try {
      await usePost(`${import.meta.env.VITE_APP_URL}/api/auth/logout`)
      useLogoutRedirect()
    } catch (error) {
      console.error('Error during logout:', error)
    }
  }

  async function exchangeToken(clientId: string) {
    try {
      return await usePost(`${import.meta.env.VITE_APP_URL}/api/auth/token-exchange`, {
        clientId: clientId,
      })
    } catch (error) {
      console.error('Error exchanging token:', error)
    }
  }

  async function generateDynamicRoutes() {
    const { menuData: treeMenuData, routeData } = await generateRoutes()
    menuData.value = treeMenuData
    routerData.value = {
      ...rootRoute,
      children: generateFlatRoutes(routeData),
    }
    return routerData.value
  }

  return {
    // State
    user,
    isAuthenticated,
    isRefreshingToken,
    showModalChangePassword,
    mqttToken,
    memberCompanyId,
    memberCompanyCode,
    menuData,
    routerData,
    // Getters
    isSignedIn,
    // Actions
    getAuthInfo,
    setUser,
    setShowModalChangePassword,
    setMqttToken,
    getUser,
    hasUri,
    changePassword,
    logout,
    exchangeToken,
    generateDynamicRoutes,
  }
})
